import { ReactNode, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

/**
 * DataTable — sortable, dense data table.
 *
 * Derived from M3 Data Table (Dense variant) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/data-table/overview
 *   - Reference: github.com/material-components/material-web (no first-party
 *                data-table at time of authoring; TanStack Table is the
 *                headless engine of choice; M3 spec drives the visual layer)
 *
 * Populus adaptations applied (per Populus/doctrine/design-system.md):
 *   - Compact density: 40px row height (vs M3 default 56px)
 *   - Brand-primary row hover background (`var(--brand-primary-bg)` — follows org)
 *   - Status column accepts any ReactNode (Badge atom typically passed in)
 *   - Optional row click handler (cursor:pointer + on-press feedback)
 *   - Column header uppercase Label-Small per M3 convention
 *
 * Token discipline:
 *   - Row hover background → --brand-primary-bg (brand surface)
 *   - Borders, text, padding → --color-* (system)
 *
 * Phase 11 Block 6 PR 3c (2026-05-12).
 */

export interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  onRowClick?: (row: TData) => void;
  emptyMessage?: ReactNode;
}

export function DataTable<TData>({
  data,
  columns,
  onRowClick,
  emptyMessage = 'No rows',
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      overflow: 'hidden',
      boxShadow: 'var(--sh-sm)',
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'auto',
      }}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} style={{
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
            }}>
              {headerGroup.headers.map(header => {
                const isSortable = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                    style={{
                      textAlign: 'left',
                      padding: '10px 16px',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 'var(--fw-semibold)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--ls-wider)',
                      color: 'var(--color-ink-3)',
                      cursor: isSortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {isSortable && (
                      <span style={{ marginLeft: 'var(--sp-1)', color: 'var(--color-muted)' }}>
                        {sortDir === 'asc' ? '↑' : sortDir === 'desc' ? '↓' : ''}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{
                padding: 'var(--sp-7) var(--sp-4)',
                textAlign: 'center',
                color: 'var(--color-muted)',
                fontSize: 'var(--fs-md)',
              }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  height: 40,  // compact density per doctrine
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background 120ms ease',
                }}
                onMouseEnter={(e) => {
                  if (onRowClick) e.currentTarget.style.background = 'var(--brand-primary-bg)';
                }}
                onMouseLeave={(e) => {
                  if (onRowClick) e.currentTarget.style.background = '';
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} style={{
                    padding: '8px 16px',
                    fontSize: 'var(--fs-md)',
                    color: 'var(--color-ink)',
                    verticalAlign: 'middle',
                  }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
